import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useUserAuth } from "../../context/userAuthContext";
import { db } from "../../firebase";
import { doc, getDoc, collection, query, where, getDocs } from "firebase/firestore";
import { Clock, Calendar, User, Tag, ArrowLeft, ThumbsUp, Share2, Bookmark, MessageSquare } from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.bubble.css";

// Traducciones para los textos
const translations = {
    en: {
        loading: "Loading article...",
        notFound: "Article not found",
        by: "By",
        publishedOn: "Published on",
        readTime: "min read",
        backToBlogs: "Back to Blogs",
        relatedArticles: "Related Articles",
        comments: "Comments",
        writeComment: "Write a comment...",
        submit: "Submit",
        share: "Share",
        save: "Save",
        like: "Like",
        categoriesTitle: "Category",
        tagsTitle: "Tags",
        loginToInteract: "Login to like, save or comment",
    },
    es: {
        loading: "Cargando artículo...",
        notFound: "Artículo no encontrado",
        by: "Por",
        publishedOn: "Publicado el",
        readTime: "min de lectura",
        backToBlogs: "Volver a Blogs",
        relatedArticles: "Artículos Relacionados",
        comments: "Comentarios",
        writeComment: "Escribe un comentario...",
        submit: "Enviar",
        share: "Compartir",
        save: "Guardar",
        like: "Me gusta",
        categoriesTitle: "Categoría",
        tagsTitle: "Etiquetas",
        loginToInteract: "Inicia sesión para dar me gusta, guardar o comentar",
    },
};

// Colores del logo de Fronios (mantener consistencia con BlogMaker)
const logoColors = {
    blue: "#0088D1", // Azul
    teal: "#33B8B8", // Verde-azulado
    orange: "#F08234", // Naranja
    green: "#6CC24A", // Verde
    darkBlue: "#0F3246", // Azul oscuro del centro
};

// Configuración simplificada de ReactQuill sin módulos de resaltado
const quillModules = {
    syntax: false, // Deshabilitamos el módulo de sintaxis que requiere highlight.js
    toolbar: false, // Sin barra de herramientas en modo lectura
};

const BlogArticleView = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { user, currentLanguage } = useUserAuth();
    const t = translations[currentLanguage || "en"];

    const [article, setArticle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [relatedArticles, setRelatedArticles] = useState([]);
    const [liked, setLiked] = useState(false);
    const [saved, setSaved] = useState(false);

    // Formatear fecha
    const formatDate = (timestamp) => {
        if (!timestamp) return "";
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return new Intl.DateTimeFormat(currentLanguage === "es" ? "es-ES" : "en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        }).format(date);
    };

    // Calcular tiempo de lectura
    const calculateReadTime = (content) => {
        if (!content) return 1;
        // Eliminar etiquetas HTML para contar solo texto
        const textOnly = content.replace(/<[^>]*>/g, "");
        const words = textOnly.split(/\s+/).length;
        // Promedio de lectura: 200 palabras por minuto
        const readTimeMinutes = Math.max(1, Math.ceil(words / 200));
        return readTimeMinutes;
    };

    // Cargar artículo y artículos relacionados
    useEffect(() => {
        const loadArticle = async () => {
            try {
                setLoading(true);

                // Buscar artículo por slug
                const blogsRef = collection(db, "blogs");
                const q = query(blogsRef, where("slug", "==", slug), where("published", "==", true));
                const querySnapshot = await getDocs(q);

                if (querySnapshot.empty) {
                    setLoading(false);
                    return;
                }

                const articleData = querySnapshot.docs[0].data();
                articleData.id = querySnapshot.docs[0].id;
                setArticle(articleData);

                // Cargar artículos relacionados por categoría
                if (articleData.category) {
                    const relatedQuery = query(
                        blogsRef,
                        where("category", "==", articleData.category),
                        where("published", "==", true),
                        where("slug", "!=", slug)
                    );

                    const relatedSnapshot = await getDocs(relatedQuery);
                    const relatedData = relatedSnapshot.docs
                        .map(doc => ({ id: doc.id, ...doc.data() }))
                        .slice(0, 3); // Limitar a 3 artículos relacionados

                    setRelatedArticles(relatedData);
                }
            } catch (error) {
                console.error("Error loading article:", error);
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            loadArticle();
        }
    }, [slug]);

    const handleLike = () => {
        if (!user) {
            // Redireccionar a login o mostrar mensaje
            alert(t.loginToInteract);
            return;
        }
        setLiked(!liked);
        // Aquí se implementaría la lógica para guardar el "me gusta" en la base de datos
    };

    const handleSave = () => {
        if (!user) {
            // Redireccionar a login o mostrar mensaje
            alert(t.loginToInteract);
            return;
        }
        setSaved(!saved);
        // Aquí se implementaría la lógica para guardar el artículo para el usuario
    };

    const handleShare = () => {
        // Implementar compartir a través de la API Web Share si está disponible
        if (navigator.share) {
            navigator.share({
                title: article.title,
                text: article.summary,
                url: window.location.href,
            });
        } else {
            // Fallback: copiar al portapapeles
            navigator.clipboard.writeText(window.location.href);
            alert(currentLanguage === "es" ? "¡Enlace copiado!" : "Link copied!");
        }
    };

    if (loading) {
        return (
            <div className="bg-gray-50 min-h-screen pt-24 pb-12 flex items-center justify-center">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-gray-600">{t.loading}</p>
                </div>
            </div>
        );
    }

    if (!article) {
        return (
            <div className="bg-gray-50 min-h-screen pt-24 pb-12 flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-2xl font-bold text-gray-800 mb-4">{t.notFound}</h1>
                    <button
                        onClick={() => navigate("/blog")}
                        className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
                    >
                        {t.backToBlogs}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen pt-24 pb-12">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
                {/* Navegación y acciones */}
                <div className="flex justify-between items-center mb-6">
                    <button
                        onClick={() => navigate("/blog")}
                        className="flex items-center text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeft className="w-5 h-5 mr-1" />
                        {t.backToBlogs}
                    </button>

                    <div className="flex space-x-3">
                        <button
                            onClick={handleLike}
                            className={`flex items-center p-2 rounded-full transition ${liked ? "text-red-500" : "text-gray-500 hover:text-red-500"
                                }`}
                        >
                            <ThumbsUp className="w-5 h-5" />
                        </button>

                        <button
                            onClick={handleSave}
                            className={`flex items-center p-2 rounded-full transition ${saved ? "text-blue-500" : "text-gray-500 hover:text-blue-500"
                                }`}
                        >
                            <Bookmark className="w-5 h-5" />
                        </button>

                        <button
                            onClick={handleShare}
                            className="flex items-center p-2 text-gray-500 hover:text-blue-500 rounded-full transition"
                        >
                            <Share2 className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Contenido principal */}
                <div className="bg-white rounded-lg shadow-md overflow-hidden mb-10">
                    {/* Imagen de portada */}
                    {article.coverImage && (
                        <div className="w-full h-72 sm:h-96 relative">
                            <img
                                src={article.coverImage}
                                alt={article.title}
                                className="w-full h-full object-cover"
                            />
                        </div>
                    )}

                    <div className="p-6 sm:p-8">
                        {/* Categoría */}
                        {article.category && (
                            <div
                                className="inline-block px-3 py-1 rounded-full text-xs font-medium mb-4"
                                style={{ backgroundColor: logoColors.teal, color: "white" }}
                            >
                                {article.category}
                            </div>
                        )}

                        {/* Título y metadatos */}
                        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                            {article.title}
                        </h1>

                        <div className="flex flex-wrap items-center text-sm text-gray-500 mb-6">
                            {/* Autor */}
                            <div className="flex items-center mr-4 mb-2">
                                <User className="w-4 h-4 mr-1" />
                                <span>
                                    {t.by} {article.authorName}
                                </span>
                            </div>

                            {/* Fecha */}
                            <div className="flex items-center mr-4 mb-2">
                                <Calendar className="w-4 h-4 mr-1" />
                                <span>{formatDate(article.createdAt)}</span>
                            </div>

                            {/* Tiempo de lectura */}
                            <div className="flex items-center mb-2">
                                <Clock className="w-4 h-4 mr-1" />
                                <span>
                                    {calculateReadTime(article.content)} {t.readTime}
                                </span>
                            </div>
                        </div>

                        {/* Resumen */}
                        {article.summary && (
                            <div className="text-lg text-gray-700 mb-8 border-l-4 pl-4 border-blue-500 italic">
                                {article.summary}
                            </div>
                        )}

                        {/* Contenido principal */}
                        <div className="prose prose-lg max-w-none article-content">
                            {/* Utilizamos el innerHTML directamente si hay problemas con ReactQuill */}
                            <div dangerouslySetInnerHTML={{ __html: article.content }} />
                        </div>

                        {/* Etiquetas */}
                        {article.tags && article.tags.length > 0 && (
                            <div className="mt-8 border-t pt-6">
                                <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center">
                                    <Tag className="w-5 h-5 mr-2" />
                                    {t.tagsTitle}
                                </h3>
                                <div className="flex flex-wrap gap-2">
                                    {article.tags.map((tag, index) => (
                                        <div
                                            key={index}
                                            className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                                        >
                                            {tag}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Artículos relacionados */}
                {relatedArticles.length > 0 && (
                    <div className="mt-12">
                        <h2 className="text-2xl font-bold text-gray-800 mb-6">
                            {t.relatedArticles}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {relatedArticles.map((related) => (
                                <div
                                    key={related.id}
                                    className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transition transform hover:-translate-y-1"
                                    onClick={() => navigate(`/blog/${related.slug}`)}
                                >
                                    {related.coverImage && (
                                        <div className="h-40 overflow-hidden">
                                            <img
                                                src={related.coverImage}
                                                alt={related.title}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                    )}
                                    <div className="p-4">
                                        {related.category && (
                                            <div
                                                className="inline-block px-2 py-1 rounded-full text-xs font-medium mb-2"
                                                style={{ backgroundColor: logoColors.teal, color: "white" }}
                                            >
                                                {related.category}
                                            </div>
                                        )}
                                        <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2">
                                            {related.title}
                                        </h3>
                                        {related.summary && (
                                            <p className="text-gray-600 text-sm line-clamp-3">
                                                {related.summary}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Sistema de comentarios - Para usuarios autenticados */}
                <div className="mt-12 bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center">
                        <MessageSquare className="w-5 h-5 mr-2" />
                        {t.comments}
                    </h2>

                    {user ? (
                        /* Formulario de comentarios para usuarios autenticados */
                        <div className="mb-8">
                            <textarea
                                placeholder={t.writeComment}
                                className="w-full p-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 resize-none h-24"
                            ></textarea>
                            <div className="mt-2 flex justify-end">
                                <button
                                    className="px-4 py-2 text-white rounded-md"
                                    style={{ backgroundColor: logoColors.blue }}
                                >
                                    {t.submit}
                                </button>
                            </div>
                        </div>
                    ) : (
                        /* Mensaje para usuarios no autenticados */
                        <div className="bg-gray-100 p-4 rounded-md mb-8 text-center">
                            <p className="text-gray-600">{t.loginToInteract}</p>
                            <button
                                onClick={() => navigate("/login")}
                                className="mt-2 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition"
                            >
                                Login
                            </button>
                        </div>
                    )}

                    {/* Lista de comentarios - Para implementar en el futuro */}
                    <div className="space-y-6 border-t pt-6">
                        <p className="text-gray-500 text-center italic">
                            {currentLanguage === "es"
                                ? "Sistema de comentarios en desarrollo"
                                : "Comment system in development"}
                        </p>
                    </div>
                </div>
            </div>

            {/* Estilos CSS para la página del artículo */}
            <style jsx="true">{`
        /* Estilos para el contenido del artículo */
        .article-content img {
          max-width: 100%;
          height: auto;
          border-radius: 0.375rem;
          margin: 1.5rem 0;
        }
        
        .article-content h1, .article-content h2, .article-content h3, 
        .article-content h4, .article-content h5, .article-content h6 {
          margin-top: 1.5rem;
          margin-bottom: 1rem;
          font-weight: 600;
          line-height: 1.25;
        }
        
        .article-content h1 {
          font-size: 2rem;
        }
        
        .article-content h2 {
          font-size: 1.5rem;
        }
        
        .article-content h3 {
          font-size: 1.25rem;
        }
        
        .article-content p {
          margin-bottom: 1rem;
          line-height: 1.625;
        }
        
        .article-content ul, .article-content ol {
          margin-bottom: 1rem;
          padding-left: 2rem;
        }
        
        .article-content ul {
          list-style-type: disc;
        }
        
        .article-content ol {
          list-style-type: decimal;
        }
        
        .article-content li {
          margin-bottom: 0.5rem;
        }
        
        .article-content blockquote {
          border-left: 4px solid #e2e8f0;
          padding-left: 1rem;
          margin-left: 0;
          margin-right: 0;
          font-style: italic;
          color: #4a5568;
        }
        
        .article-content pre {
          background-color: #2d3748;
          color: #e2e8f0;
          padding: 1rem;
          border-radius: 0.375rem;
          overflow-x: auto;
          font-family: monospace;
          margin: 1.5rem 0;
        }
        
        .article-content code {
          background-color: #edf2f7;
          padding: 0.2rem 0.4rem;
          border-radius: 0.25rem;
          font-family: monospace;
          font-size: 0.875em;
        }
        
        .article-content pre code {
          background-color: transparent;
          padding: 0;
          border-radius: 0;
          color: inherit;
        }
        
        /* Estilos para los bloques de código personalizados del BlogMaker */
        .article-content .code-snippet {
          margin: 1.5rem 0;
          border-radius: 0.5rem;
          overflow: hidden;
        }
        
        .article-content .code-header {
          background-color: #2d3748;
          color: #e2e8f0;
          padding: 0.5rem 1rem;
          font-size: 0.875rem;
          font-family: monospace;
        }
        
        /* Limitar líneas para descripciones */
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        .line-clamp-3 {
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
        </div>
    );
};

export default BlogArticleView;